import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { catalogService } from '../../../services/catalogService';
import { 
  BASE_CANVAS,
  mouseToBaseCoordinates,
  baseToRenderedCoordinates,
  getHotspotStyle,
  clampCoordinates,
  getRenderedDimensions
} from '../../../utils/coordinateSystem';

const PageCanvas = ({ 
  currentPage, 
  totalPages, 
  pages,
  onPageChange, 
  hotspots, 
  onHotspotAdd,
  onHotspotUpdate,
  onHotspotSelect,
  selectedHotspot,
  zoom,
  onZoomChange,
  creatingHotspot
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
  const [imageLoading, setImageLoading] = useState({ left: true, right: true });
  const [imageError, setImageError] = useState({ left: false, right: false });
  const [draggingHotspot, setDraggingHotspot] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [retryCount, setRetryCount] = useState(0);
  const imageRef = useRef({ left: null, right: null });
  const [currentImageUrls, setCurrentImageUrls] = useState({ left: null, right: null });
  const [loadingPageUrl, setLoadingPageUrl] = useState({ left: false, right: false });
  const pageContainerRefs = useRef({ left: null, right: null });
  
  // NEW: Drawing state for draw-to-create interaction
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [drawingPage, setDrawingPage] = useState(null);
  // Refs mirror drawing state to avoid stale closure in window event callbacks
  const isDrawingRef = useRef(false);
  const drawStartRef = useRef(null);
  const drawingPageRef = useRef(null);
  
  // NEW: Resizing state
  const [resizingHotspot, setResizingHotspot] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState(null);

  // MAGAZINE LAYOUT LOGIC
  // Page 1 = Front cover (single)
  // Pages 2-3, 4-5, 6-7... = Spreads
  // Last page = Back cover (single)
  const isFrontCover = currentPage === 1;
  const isBackCover = currentPage === totalPages;
  const isSpread = !isFrontCover && !isBackCover;

  // Get pages to display
  const getDisplayPages = () => {
    if (isFrontCover) {
      return { left: null, right: pages?.find(p => p?.pageNumber === 1) };
    } else if (isBackCover) {
      return { left: pages?.find(p => p?.pageNumber === totalPages), right: null };
    } else {
      // For spreads: ensure even page on left, odd on right
      // Page 2 pairs with 3, 4 with 5, etc.
      const leftPageNum = currentPage % 2 === 0 ? currentPage : currentPage - 1;
      const rightPageNum = leftPageNum + 1;
      return {
        left: pages?.find(p => p?.pageNumber === leftPageNum),
        right: pages?.find(p => p?.pageNumber === rightPageNum)
      };
    }
  };

  const { left: leftPageData, right: rightPageData } = getDisplayPages();

  useEffect(() => {
    const updateDimensions = () => {
      if (canvasRef?.current) {
        const rect = canvasRef?.current?.getBoundingClientRect();
        setCanvasDimensions({ width: rect?.width, height: rect?.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Lazy load page images when page changes
  useEffect(() => {
    const loadPageImages = async () => {
      setImageLoading({ left: !!leftPageData, right: !!rightPageData });
      setImageError({ left: false, right: false });
      setRetryCount(0);

      const loadImage = async (pageData, side) => {
        if (!pageData) {
          setCurrentImageUrls(prev => ({ ...prev, [side]: null }));
          setImageLoading(prev => ({ ...prev, [side]: false }));
          return;
        }

        // If imageUrl already exists (preloaded), use it
        if (pageData?.imageUrl) {
          setCurrentImageUrls(prev => ({ ...prev, [side]: pageData?.imageUrl }));
          setImageLoading(prev => ({ ...prev, [side]: false }));
          return;
        }

        // Otherwise, lazy load the image URL
        if (pageData?.imagePath) {
          try {
            setLoadingPageUrl(prev => ({ ...prev, [side]: true }));
            const url = await catalogService?.getPageImageUrl(pageData?.imagePath);
            setCurrentImageUrls(prev => ({ ...prev, [side]: url }));
          } catch (error) {
            console.error(`Failed to load image URL for page ${pageData?.pageNumber}:`, error);
            setImageError(prev => ({ ...prev, [side]: true }));
            setImageLoading(prev => ({ ...prev, [side]: false }));
          } finally {
            setLoadingPageUrl(prev => ({ ...prev, [side]: false }));
          }
        } else {
          setImageError(prev => ({ ...prev, [side]: true }));
          setImageLoading(prev => ({ ...prev, [side]: false }));
        }
      };

      await Promise.all([
        loadImage(leftPageData, 'left'),
        loadImage(rightPageData, 'right')
      ]);
    };

    loadPageImages();
  }, [currentPage, leftPageData, rightPageData]);

  const handleImageLoad = (side) => {
    setImageLoading(prev => ({ ...prev, [side]: false }));
    setImageError(prev => ({ ...prev, [side]: false }));
  };

  const handleImageError = (side, pageNumber) => {
    console.error(`Failed to load image for page ${pageNumber}`);
    setImageLoading(prev => ({ ...prev, [side]: false }));
    setImageError(prev => ({ ...prev, [side]: true }));
  };

  const handleRetry = async (side, pageData) => {
    setImageLoading(prev => ({ ...prev, [side]: true }));
    setImageError(prev => ({ ...prev, [side]: false }));
    setRetryCount(prev => prev + 1);
    
    if (pageData?.imagePath) {
      try {
        setLoadingPageUrl(prev => ({ ...prev, [side]: true }));
        const url = await catalogService?.getPageImageUrl(pageData?.imagePath);
        setCurrentImageUrls(prev => ({ ...prev, [side]: `${url}?retry=${retryCount + 1}` }));
      } catch (error) {
        console.error(`Retry failed for page ${pageData?.pageNumber}:`, error);
        setImageError(prev => ({ ...prev, [side]: true }));
        setImageLoading(prev => ({ ...prev, [side]: false }));
      } finally {
        setLoadingPageUrl(prev => ({ ...prev, [side]: false }));
      }
    }
  };

  // NEW: Handle mouse down on canvas - start drawing
  const handleCanvasMouseDown = (e, pageNumber) => {
    if (draggingHotspot || resizingHotspot) return;

    const pageContainer = e?.currentTarget;
    const baseCoords = mouseToBaseCoordinates(e, pageContainer);

    isDrawingRef.current = true;
    drawStartRef.current = baseCoords;
    drawingPageRef.current = pageNumber;

    setIsDrawing(true);
    setDrawStart(baseCoords);
    setDrawCurrent(baseCoords);
    setDrawingPage(pageNumber);

    // Register listeners IMMEDIATELY (synchronous) so they catch even a very
    // quick mouseup that fires before React's next render + useEffect cycle.
    const onMove = (ev) => {
      if (!isDrawingRef.current) return;
      const container = document.querySelector(`[data-page-number="${drawingPageRef.current}"]`);
      if (!container) return;
      setDrawCurrent(mouseToBaseCoordinates(ev, container));
    };

    const onUp = (ev) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);

      const start = drawStartRef.current;
      const page = drawingPageRef.current;

      isDrawingRef.current = false;
      drawStartRef.current = null;
      drawingPageRef.current = null;
      setIsDrawing(false);
      setDrawStart(null);
      setDrawCurrent(null);
      setDrawingPage(null);

      if (!start || !page) return;

      const container = document.querySelector(`[data-page-number="${page}"]`);
      if (!container) return;

      const endCoords = mouseToBaseCoordinates(ev, container);
      const x = Math.min(start.x, endCoords.x);
      const y = Math.min(start.y, endCoords.y);
      const width = Math.abs(endCoords.x - start.x);
      const height = Math.abs(endCoords.y - start.y);

      const clampedCoords = clampCoordinates({ x, y }, { width, height });
      const hotspotData = {
        x: clampedCoords.x,
        y: clampedCoords.y,
        width: Math.min(width, BASE_CANVAS.WIDTH - clampedCoords.x),
        height: Math.min(height, BASE_CANVAS.HEIGHT - clampedCoords.y),
        page
      };

      if (typeof onHotspotAdd === 'function') {
        onHotspotAdd(hotspotData);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Handle mouse down for dragging hotspot
  const handleHotspotMouseDown = (e, hotspot) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    const pageContainer = e?.currentTarget?.closest('.page-container');
    const { scale } = getRenderedDimensions(pageContainer);
    
    // Calculate offset in base coordinates
    const baseCoords = mouseToBaseCoordinates(e, pageContainer);
    
    setDraggingHotspot(hotspot?.id);
    setDragOffset({
      x: baseCoords?.x - hotspot?.x,
      y: baseCoords?.y - hotspot?.y
    });
    onHotspotSelect(hotspot?.id);
  };

  const handleHotspotMouseMove = useCallback((e) => {
    if (!draggingHotspot) return;
    
    const hotspot = hotspots?.find(h => h?.id === draggingHotspot);
    if (!hotspot) return;

    const pageContainer = document?.querySelector(`[data-page-number="${hotspot?.pageNumber}"]`);
    if (!pageContainer) return;

    const baseCoords = mouseToBaseCoordinates(e, pageContainer);
    const newBaseX = baseCoords?.x - dragOffset?.x;
    const newBaseY = baseCoords?.y - dragOffset?.y;
    
    const hotspotSize = { 
      width: hotspot?.width || 200, 
      height: hotspot?.height || 200 
    };
    
    const clampedCoords = clampCoordinates({ x: newBaseX, y: newBaseY }, hotspotSize);
    
    // Update visual position using rendered coordinates
    const renderedCoords = baseToRenderedCoordinates(clampedCoords, pageContainer);
    const hotspotElement = document?.querySelector(`[data-hotspot-id="${draggingHotspot}"]`);
    if (hotspotElement) {
      hotspotElement.style.left = `${renderedCoords?.x}px`;
      hotspotElement.style.top = `${renderedCoords?.y}px`;
    }
  }, [draggingHotspot, dragOffset, hotspots]);

  const handleHotspotMouseUp = useCallback(async (e) => {
    if (!draggingHotspot) return;
    
    const hotspot = hotspots?.find(h => h?.id === draggingHotspot);
    if (!hotspot) return;

    const pageContainer = document?.querySelector(`[data-page-number="${hotspot?.pageNumber}"]`);
    if (!pageContainer) return;

    const baseCoords = mouseToBaseCoordinates(e, pageContainer);
    const newBaseX = baseCoords?.x - dragOffset?.x;
    const newBaseY = baseCoords?.y - dragOffset?.y;
    
    const hotspotSize = { 
      width: hotspot?.width || 200, 
      height: hotspot?.height || 200 
    };
    
    const clampedCoords = clampCoordinates({ x: newBaseX, y: newBaseY }, hotspotSize);
    
    if (onHotspotUpdate) {
      await onHotspotUpdate(draggingHotspot, { x: clampedCoords?.x, y: clampedCoords?.y });
    }
    
    setDraggingHotspot(null);
    setDragOffset({ x: 0, y: 0 });
  }, [draggingHotspot, dragOffset, hotspots, onHotspotUpdate]);

  // NEW: Handle resize mouse down
  const handleResizeMouseDown = (e, hotspot, handle) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    const pageContainer = document?.querySelector(`[data-page-number="${hotspot?.pageNumber}"]`);
    if (!pageContainer) return;
    
    const baseCoords = mouseToBaseCoordinates(e, pageContainer);
    
    setResizingHotspot(hotspot?.id);
    setResizeHandle(handle);
    setResizeStart({
      x: baseCoords?.x,
      y: baseCoords?.y,
      hotspotX: hotspot?.x,
      hotspotY: hotspot?.y,
      hotspotWidth: hotspot?.width || 200,
      hotspotHeight: hotspot?.height || 200
    });
    onHotspotSelect(hotspot?.id);
  };

  // NEW: Handle resize mouse move
  const handleResizeMouseMove = useCallback((e) => {
    if (!resizingHotspot || !resizeStart || !resizeHandle) return;
    
    const hotspot = hotspots?.find(h => h?.id === resizingHotspot);
    if (!hotspot) return;
    
    const pageContainer = document?.querySelector(`[data-page-number="${hotspot?.pageNumber}"]`);
    if (!pageContainer) return;
    
    const baseCoords = mouseToBaseCoordinates(e, pageContainer);
    const deltaX = baseCoords?.x - resizeStart?.x;
    const deltaY = baseCoords?.y - resizeStart?.y;
    
    let newX = resizeStart?.hotspotX;
    let newY = resizeStart?.hotspotY;
    let newWidth = resizeStart?.hotspotWidth;
    let newHeight = resizeStart?.hotspotHeight;
    
    // Calculate new dimensions based on handle
    switch (resizeHandle) {
      case 'nw': // Top-left
        newX = resizeStart?.hotspotX + deltaX;
        newY = resizeStart?.hotspotY + deltaY;
        newWidth = resizeStart?.hotspotWidth - deltaX;
        newHeight = resizeStart?.hotspotHeight - deltaY;
        break;
      case 'ne': // Top-right
        newY = resizeStart?.hotspotY + deltaY;
        newWidth = resizeStart?.hotspotWidth + deltaX;
        newHeight = resizeStart?.hotspotHeight - deltaY;
        break;
      case 'sw': // Bottom-left
        newX = resizeStart?.hotspotX + deltaX;
        newWidth = resizeStart?.hotspotWidth - deltaX;
        newHeight = resizeStart?.hotspotHeight + deltaY;
        break;
      case 'se': // Bottom-right
        newWidth = resizeStart?.hotspotWidth + deltaX;
        newHeight = resizeStart?.hotspotHeight + deltaY;
        break;
      case 'n': // Top edge
        newY = resizeStart?.hotspotY + deltaY;
        newHeight = resizeStart?.hotspotHeight - deltaY;
        break;
      case 's': // Bottom edge
        newHeight = resizeStart?.hotspotHeight + deltaY;
        break;
      case 'w': // Left edge
        newX = resizeStart?.hotspotX + deltaX;
        newWidth = resizeStart?.hotspotWidth - deltaX;
        break;
      case 'e': // Right edge
        newWidth = resizeStart?.hotspotWidth + deltaX;
        break;
    }
    
    // Enforce minimum size
    const minSize = 20;
    if (newWidth < minSize) {
      if (resizeHandle?.includes('w')) {
        newX = resizeStart?.hotspotX + resizeStart?.hotspotWidth - minSize;
      }
      newWidth = minSize;
    }
    if (newHeight < minSize) {
      if (resizeHandle?.includes('n')) {
        newY = resizeStart?.hotspotY + resizeStart?.hotspotHeight - minSize;
      }
      newHeight = minSize;
    }
    
    // Clamp to canvas bounds
    newX = Math.max(0, Math.min(newX, BASE_CANVAS?.WIDTH - newWidth));
    newY = Math.max(0, Math.min(newY, BASE_CANVAS?.HEIGHT - newHeight));
    newWidth = Math.min(newWidth, BASE_CANVAS?.WIDTH - newX);
    newHeight = Math.min(newHeight, BASE_CANVAS?.HEIGHT - newY);
    
    // Update visual position and size
    const renderedCoords = baseToRenderedCoordinates({ x: newX, y: newY }, pageContainer);
    const { scale } = getRenderedDimensions(pageContainer);
    const hotspotElement = document?.querySelector(`[data-hotspot-id="${resizingHotspot}"]`);
    if (hotspotElement) {
      hotspotElement.style.left = `${renderedCoords?.x}px`;
      hotspotElement.style.top = `${renderedCoords?.y}px`;
      hotspotElement.style.width = `${Math.round(newWidth * scale)}px`;
      hotspotElement.style.height = `${Math.round(newHeight * scale)}px`;
    }
  }, [resizingHotspot, resizeStart, resizeHandle, hotspots]);

  // NEW: Handle resize mouse up
  const handleResizeMouseUp = useCallback(async (e) => {
    if (!resizingHotspot || !resizeStart || !resizeHandle) return;
    
    const hotspot = hotspots?.find(h => h?.id === resizingHotspot);
    if (!hotspot) return;
    
    const pageContainer = document?.querySelector(`[data-page-number="${hotspot?.pageNumber}"]`);
    if (!pageContainer) return;
    
    const baseCoords = mouseToBaseCoordinates(e, pageContainer);
    const deltaX = baseCoords?.x - resizeStart?.x;
    const deltaY = baseCoords?.y - resizeStart?.y;
    
    let newX = resizeStart?.hotspotX;
    let newY = resizeStart?.hotspotY;
    let newWidth = resizeStart?.hotspotWidth;
    let newHeight = resizeStart?.hotspotHeight;
    
    // Calculate final dimensions
    switch (resizeHandle) {
      case 'nw':
        newX = resizeStart?.hotspotX + deltaX;
        newY = resizeStart?.hotspotY + deltaY;
        newWidth = resizeStart?.hotspotWidth - deltaX;
        newHeight = resizeStart?.hotspotHeight - deltaY;
        break;
      case 'ne':
        newY = resizeStart?.hotspotY + deltaY;
        newWidth = resizeStart?.hotspotWidth + deltaX;
        newHeight = resizeStart?.hotspotHeight - deltaY;
        break;
      case 'sw':
        newX = resizeStart?.hotspotX + deltaX;
        newWidth = resizeStart?.hotspotWidth - deltaX;
        newHeight = resizeStart?.hotspotHeight + deltaY;
        break;
      case 'se':
        newWidth = resizeStart?.hotspotWidth + deltaX;
        newHeight = resizeStart?.hotspotHeight + deltaY;
        break;
      case 'n':
        newY = resizeStart?.hotspotY + deltaY;
        newHeight = resizeStart?.hotspotHeight - deltaY;
        break;
      case 's':
        newHeight = resizeStart?.hotspotHeight + deltaY;
        break;
      case 'w':
        newX = resizeStart?.hotspotX + deltaX;
        newWidth = resizeStart?.hotspotWidth - deltaX;
        break;
      case 'e':
        newWidth = resizeStart?.hotspotWidth + deltaX;
        break;
    }
    
    // Enforce minimum size
    const minSize = 20;
    if (newWidth < minSize) {
      if (resizeHandle?.includes('w')) {
        newX = resizeStart?.hotspotX + resizeStart?.hotspotWidth - minSize;
      }
      newWidth = minSize;
    }
    if (newHeight < minSize) {
      if (resizeHandle?.includes('n')) {
        newY = resizeStart?.hotspotY + resizeStart?.hotspotHeight - minSize;
      }
      newHeight = minSize;
    }
    
    // Clamp to canvas bounds
    newX = Math.max(0, Math.min(newX, BASE_CANVAS?.WIDTH - newWidth));
    newY = Math.max(0, Math.min(newY, BASE_CANVAS?.HEIGHT - newHeight));
    newWidth = Math.min(newWidth, BASE_CANVAS?.WIDTH - newX);
    newHeight = Math.min(newHeight, BASE_CANVAS?.HEIGHT - newY);
    
    if (onHotspotUpdate) {
      await onHotspotUpdate(resizingHotspot, { 
        x: Math.round(newX), 
        y: Math.round(newY),
        width: Math.round(newWidth),
        height: Math.round(newHeight)
      });
    }
    
    setResizingHotspot(null);
    setResizeHandle(null);
    setResizeStart(null);
  }, [resizingHotspot, resizeStart, resizeHandle, hotspots, onHotspotUpdate]);

  // Global mouse event listeners
  useEffect(() => {
    if (draggingHotspot) {
      window.addEventListener('mousemove', handleHotspotMouseMove);
      window.addEventListener('mouseup', handleHotspotMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleHotspotMouseMove);
        window.removeEventListener('mouseup', handleHotspotMouseUp);
      };
    }
  }, [draggingHotspot, dragOffset]);

  useEffect(() => {
    if (resizingHotspot) {
      window.addEventListener('mousemove', handleResizeMouseMove);
      window.addEventListener('mouseup', handleResizeMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleResizeMouseMove);
        window.removeEventListener('mouseup', handleResizeMouseUp);
      };
    }
  }, [resizingHotspot, resizeHandle, resizeStart]);

  // Cleanup on unmount: null out DOM refs to prevent detached node retention
  useEffect(() => {
    return () => {
      pageContainerRefs.current.left = null;
      pageContainerRefs.current.right = null;
      if (imageRef?.current) {
        imageRef.current.left = null;
        imageRef.current.right = null;
      }
    };
  }, []);

  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    const youtubeMatch = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch?.[1]}`;
    }
    
    const vimeoMatch = url?.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch?.[1]}`;
    }
    
    return url;
  };

  // NEW: Render resize handles
  const renderResizeHandles = (hotspot, pageContainer) => {
    if (selectedHotspot !== hotspot?.id) return null;
    
    const handles = [
      { name: 'nw', cursor: 'nw-resize', position: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2' },
      { name: 'ne', cursor: 'ne-resize', position: 'top-0 right-0 translate-x-1/2 -translate-y-1/2' },
      { name: 'sw', cursor: 'sw-resize', position: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2' },
      { name: 'se', cursor: 'se-resize', position: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2' },
      { name: 'n', cursor: 'n-resize', position: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2' },
      { name: 's', cursor: 's-resize', position: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2' },
      { name: 'w', cursor: 'w-resize', position: 'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2' },
      { name: 'e', cursor: 'e-resize', position: 'top-1/2 right-0 translate-x-1/2 -translate-y-1/2' }
    ];
    
    return handles?.map(handle => (
      <div
        key={handle?.name}
        onMouseDown={(e) => handleResizeMouseDown(e, hotspot, handle?.name)}
        className={`absolute w-3 h-3 bg-white border-2 border-blue-500 rounded-full z-[60] ${handle?.position}`}
        style={{ cursor: handle?.cursor }}
      />
    ));
  };

  const renderHotspot = (hotspot, pageNumber, side) => {
    const isSelected = selectedHotspot === hotspot?.id;
    const isDraggingThis = draggingHotspot === hotspot?.id;
    const isResizingThis = resizingHotspot === hotspot?.id;
    
    // Get style using unified coordinate system
    const pageContainer = pageContainerRefs?.current?.[side];
    const style = pageContainer 
      ? getHotspotStyle(hotspot, pageContainer)
      : {
          left: `${hotspot?.x}px`,
          top: `${hotspot?.y}px`,
          width: `${hotspot?.width || 200}px`,
          height: `${hotspot?.height || 200}px`
        };
    
    // Render image hotspot with border and label
    if (hotspot?.type === 'image') {
      return (
        <div
          key={hotspot?.id}
          data-hotspot-id={hotspot?.id}
          onMouseDown={(e) => handleHotspotMouseDown(e, hotspot)}
          onClick={(e) => {
            e?.stopPropagation();
            if (!isDraggingThis && !isResizingThis) {
              onHotspotSelect(hotspot?.id);
            }
          }}
          className={`absolute transition-all duration-200 z-50 ${
            isSelected ? 'ring-4 ring-blue-500' : 'ring-2 ring-blue-400 hover:ring-blue-500'
          } ${isDraggingThis || isResizingThis ? 'cursor-grabbing opacity-80' : 'cursor-grab'}`}
          style={style}
        >
          {hotspot?.imageUrl ? (
            <img
              src={hotspot?.imageUrl}
              alt={hotspot?.title || 'Hotspot image'}
              className="w-full h-full object-cover rounded-lg shadow-lg pointer-events-none"
            />
          ) : (
            <div className="w-full h-full bg-blue-100 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center">
              <Icon name="Image" size={24} className="text-blue-600" />
            </div>
          )}
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-md z-10 pointer-events-none">
            Image
          </div>
          {renderResizeHandles(hotspot, pageContainer)}
        </div>
      );
    }
    
    // Render video hotspot with border and label
    if (hotspot?.type === 'video') {
      const embedUrl = getEmbedUrl(hotspot?.videoUrl);
      const isDirectVideo = embedUrl === hotspot?.videoUrl && (embedUrl?.endsWith('.mp4') || embedUrl?.endsWith('.webm') || embedUrl?.endsWith('.ogg'));
      
      return (
        <div
          key={hotspot?.id}
          data-hotspot-id={hotspot?.id}
          onMouseDown={(e) => handleHotspotMouseDown(e, hotspot)}
          onClick={(e) => {
            e?.stopPropagation();
            if (!isDraggingThis && !isResizingThis) {
              onHotspotSelect(hotspot?.id);
            }
          }}
          className={`absolute transition-all duration-200 z-50 ${
            isSelected ? 'ring-4 ring-purple-500' : 'ring-2 ring-purple-400 hover:ring-purple-500'
          } ${isDraggingThis || isResizingThis ? 'cursor-grabbing opacity-80' : 'cursor-grab'}`}
          style={style}
        >
          {hotspot?.videoUrl ? (
            isDirectVideo ? (
              <video
                src={embedUrl}
                controls
                className="w-full h-full object-cover rounded-lg shadow-lg pointer-events-none"
              />
            ) : (
              <iframe
                src={embedUrl}
                className="w-full h-full rounded-lg shadow-lg pointer-events-none"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )
          ) : (
            <div className="w-full h-full bg-purple-100 border-2 border-dashed border-purple-400 rounded-lg flex items-center justify-center">
              <Icon name="Video" size={24} className="text-purple-600" />
            </div>
          )}
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-md z-10 pointer-events-none">
            Video
          </div>
          {renderResizeHandles(hotspot, pageContainer)}
        </div>
      );
    }

    // Render internal_link hotspot with visible border (editor-only)
    if (hotspot?.type === 'internal_link') {
      return (
        <div
          key={hotspot?.id}
          data-hotspot-id={hotspot?.id}
          onMouseDown={(e) => handleHotspotMouseDown(e, hotspot)}
          onClick={(e) => {
            e?.stopPropagation();
            if (!isDraggingThis && !isResizingThis) {
              onHotspotSelect(hotspot?.id);
            }
          }}
          className={`absolute transition-all duration-200 z-50 ${
            isSelected ? 'ring-4 ring-teal-500' : 'ring-2 ring-teal-400 hover:ring-teal-500'
          } ${isDraggingThis || isResizingThis ? 'cursor-grabbing opacity-80' : 'cursor-grab'}`}
          style={style}
        >
          <div className="w-full h-full bg-transparent border-2 border-dashed border-teal-500 rounded-lg flex items-center justify-center">
            <div className="flex flex-col items-center gap-1 pointer-events-none">
              <Icon name="ArrowRight" size={24} className="text-teal-600" />
              <span className="text-xs text-teal-600 font-medium">
                Page {hotspot?.internalLinkPage || '?'}
              </span>
            </div>
          </div>
          <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow-md z-10 pointer-events-none">
            Internal Link
          </div>
          {renderResizeHandles(hotspot, pageContainer)}
        </div>
      );
    }
    
    // Render standard button hotspot with bounding box
    return (
      <div
        key={hotspot?.id}
        data-hotspot-id={hotspot?.id}
        onMouseDown={(e) => handleHotspotMouseDown(e, hotspot)}
        onClick={(e) => {
          e?.stopPropagation();
          if (!isDraggingThis && !isResizingThis) {
            onHotspotSelect(hotspot?.id);
          }
        }}
        className={`absolute rounded-lg transition-all duration-200 z-50 flex items-center justify-center ${
          isSelected ? 'ring-4 ring-blue-500' : 'ring-2 ring-blue-400 hover:ring-blue-500'
        } ${isDraggingThis || isResizingThis ? 'cursor-grabbing opacity-80' : 'cursor-grab'}`}
        style={{
          ...style,
          backgroundColor: hotspot?.buttonColor || '#D69E2E',
          color: '#ffffff'
        }}
      >
        <span className="font-medium text-sm pointer-events-none">
          {hotspot?.buttonText || 'Click Me'}
        </span>
        {renderResizeHandles(hotspot, pageContainer)}
      </div>
    );
  };

  // NEW: Render drawing preview
  const renderDrawingPreview = (side) => {
    if (!isDrawing || !drawStart || !drawCurrent || drawingPage !== (side === 'left' ? leftPageData?.pageNumber : rightPageData?.pageNumber)) return null;
    
    const pageContainer = pageContainerRefs?.current?.[side];
    if (!pageContainer) return null;
    
    const { scale } = getRenderedDimensions(pageContainer);
    
    const x = Math.min(drawStart?.x, drawCurrent?.x);
    const y = Math.min(drawStart?.y, drawCurrent?.y);
    const width = Math.abs(drawCurrent?.x - drawStart?.x);
    const height = Math.abs(drawCurrent?.y - drawStart?.y);
    
    const renderedCoords = baseToRenderedCoordinates({ x, y }, pageContainer);
    const isTooSmall = width < 20 || height < 20;
    
    return (
      <div
        className={`absolute border-2 border-dashed z-40 pointer-events-none transition-colors ${
          isTooSmall 
            ? 'border-red-500 bg-red-100 bg-opacity-30' :'border-blue-500 bg-blue-100 bg-opacity-30'
        }`}
        style={{
          left: `${renderedCoords?.x}px`,
          top: `${renderedCoords?.y}px`,
          width: `${Math.round(width * scale)}px`,
          height: `${Math.round(height * scale)}px`
        }}
      >
        <div className={`absolute -top-6 left-0 text-white text-xs px-2 py-1 rounded whitespace-nowrap ${
          isTooSmall ? 'bg-red-600' : 'bg-blue-600'
        }`}>
          {Math.round(width)} × {Math.round(height)} px
          {isTooSmall && ' (min 20×20)'}
        </div>
      </div>
    );
  };

  // Render a single page with its hotspots
  const renderPage = (pageData, side, pageNumber) => {
    if (!pageData) return null;

    const imageUrl = currentImageUrls?.[side];
    const isLoading = imageLoading?.[side];
    const hasError = imageError?.[side];
    const isLoadingUrl = loadingPageUrl?.[side];
    const pageHotspots = hotspots?.filter(h => h?.pageNumber === pageNumber) || [];

    return (
      <div
        ref={(el) => { pageContainerRefs.current[side] = el; }}
        className="page-container relative bg-white shadow-elevation-lg rounded-lg overflow-hidden flex-1"
        data-page-number={pageNumber}
        onMouseDown={(e) => handleCanvasMouseDown(e, pageNumber)}
        style={{
          width: `${BASE_CANVAS?.WIDTH * (zoom / 100)}px`,
          height: `${BASE_CANVAS?.HEIGHT * (zoom / 100)}px`,
          minWidth: '400px',
          minHeight: '600px',
          cursor: isDrawing ? 'crosshair' : 'crosshair'
        }}
      >
        {(isLoading || isLoadingUrl) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-sm text-muted-foreground">
                {isLoadingUrl ? 'Preparing page...' : `Loading page ${pageNumber}...`}
              </p>
            </div>
          </div>
        )}
        {hasError && !isLoadingUrl && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center max-w-md px-6">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Failed to Load Page</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Unable to load page {pageNumber}.
              </p>
              <button
                onClick={() => handleRetry(side, pageData)}
                className="px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors inline-flex items-center gap-2"
              >
                <Icon name="RefreshCw" size={16} />
                Retry Loading
              </button>
            </div>
          </div>
        )}
        {imageUrl && !isLoadingUrl && (
          <img
            ref={(el) => { if (imageRef?.current) imageRef.current[side] = el; }}
            src={imageUrl}
            alt={pageData?.imageAlt || `Catalog page ${pageNumber}`}
            onLoad={() => handleImageLoad(side)}
            onError={() => handleImageError(side, pageNumber)}
            className={`w-full h-full object-contain ${isLoading || hasError ? 'hidden' : ''}`}
            loading="eager"
          />
        )}
        {!isLoading && !hasError && !isLoadingUrl && pageHotspots?.map(hotspot => 
          renderHotspot(hotspot, pageNumber, side)
        )}
        {renderDrawingPreview(side)}
        {!isLoading && !hasError && !isLoadingUrl && !imageUrl && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">No image available for this page</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Navigation helpers for magazine layout
  const handlePrevPage = () => {
    if (currentPage === 1) return;
    
    if (currentPage === 2) {
      onPageChange(1);
    } else if (isBackCover) {
      const lastSpreadStart = totalPages % 2 === 0 ? totalPages - 2 : totalPages - 1;
      onPageChange(lastSpreadStart);
    } else {
      onPageChange(Math.max(2, currentPage - 2));
    }
  };

  const handleNextPage = () => {
    if (currentPage === totalPages) return;
    
    if (isFrontCover) {
      onPageChange(2);
    } else if (currentPage === totalPages - 1 || currentPage === totalPages - 2) {
      onPageChange(totalPages);
    } else {
      onPageChange(Math.min(totalPages, currentPage + 2));
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-muted/30">
      {/* Toolbar */}
      <div className="bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="p-2 rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{t('catalogEditor.pageCanvas.page')}</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e?.target?.value);
                if (page >= 1 && page <= totalPages) {
                  onPageChange(page);
                }
              }}
              className="w-16 px-2 py-1 text-sm border border-border rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <span className="text-sm text-muted-foreground">{t('catalogEditor.pageCanvas.of')} {totalPages}</span>
          </div>
          
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <Icon name="ChevronRight" size={20} />
          </button>

          {isSpread && (
            <span className="text-xs text-muted-foreground ml-2">
              ({t('catalogEditor.pageCanvas.spread', { left: leftPageData?.pageNumber, right: rightPageData?.pageNumber })})
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => onZoomChange(Math.max(50, zoom - 10))}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Zoom out"
          >
            <Icon name="ZoomOut" size={20} />
          </button>
          
          <span className="text-sm font-medium min-w-[60px] text-center">{zoom}%</span>
          
          <button
            onClick={() => onZoomChange(Math.min(200, zoom + 10))}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Zoom in"
          >
            <Icon name="ZoomIn" size={20} />
          </button>
          
          <button
            onClick={() => onZoomChange(100)}
            className="px-3 py-1.5 text-sm rounded-lg hover:bg-muted transition-colors"
          >
            Fit
          </button>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="flex-1 overflow-auto p-8 flex items-center justify-center">
        <div
          ref={canvasRef}
          className="flex gap-4"
        >
          {/* Left Page (for spreads and back cover) */}
          {leftPageData && renderPage(leftPageData, 'left', leftPageData?.pageNumber)}
          
          {/* Right Page (for spreads and front cover) */}
          {rightPageData && renderPage(rightPageData, 'right', rightPageData?.pageNumber)}
        </div>
      </div>
    </div>
  );
};

export default PageCanvas;